import type { Badge } from "../domain/badges.js";
import { formatDate } from "../domain/dates.js";

/**
 * 成就徽章牆。
 *
 * **牆上只放圓章，名稱與條件點了才出現。** 先前一枚一張寬卡，九枚在手機上
 * 把統計頁拉長一大段，而那些條件文字平常沒有人在讀——真正每天會看的只有
 * 「拿到幾枚、還差哪幾枚」。
 *
 * 徽章是手寫 SVG，與 charts.tsx 同一路數：沒有圖片資源，配色跟著 CSS 變數走，
 * 換主題不會有一堆對不上的 png。
 */

/**
 * 肉羹的頭。從左耳尖順時針走一圈：
 * 左耳內緣 → 額頭凹口 → 右耳內緣 → 右耳尖 → 右臉頰 → 下巴 → 左臉頰 → 回左耳尖。
 *
 * **頭與耳朵是同一條路徑。** 先前用「橢圓＋兩個三角形」疊出來，頭的外框線
 * 直接畫過耳朵，看起來就是三個貼在一起的幾何圖形而不是一顆頭——耳朵必須是
 * 從頭長出來的，中間那道凹口也是輪廓的一部分。
 *
 * 比例照著肉羹：**寬臉、下巴圓、耳朵大而且在最外側**。畫窄了會變成面具，
 * 耳朵往中間靠會變成狐狸。耳尖用短弧收成圓角——他整隻都是圓角畫風。
 */
const HEAD_PATH = [
  "M3.8 14",
  "C4.6 8.6 6.2 5.4 9.2 6.4",       // 左耳外緣上到耳尖（圓角）
  "C11.6 7.2 14 9.6 16.2 11.9",     // 左耳內緣下來
  "Q25 8.8 33.8 11.9",              // 兩耳之間的額頭。是往上凸的，不是 V 形凹口
  "C36 9.6 38.4 7.2 40.8 6.4",      // 右耳內緣上去
  "C43.8 5.4 45.4 8.6 46.2 14",     // 右耳尖（圓角）接到外緣
  "C47.6 19 48 23 48 26.9",         // 右臉側，最寬處在偏下的位置
  "C48 36 39 44 25 44",             // 右臉頰與下巴
  "C11 44 2 36 2 26.9",             // 左臉頰
  "C2 23 2.4 19 3.8 14",            // 左臉側回到耳朵外緣
  "Z"
].join(" ");

/**
 * 肉羹的頭型做成徽章。
 *
 * **只有剪影，沒有五官。** 60px 下把眼睛鼻子嘴巴鬍鬚全塞進去只會糊成一團，
 * 而且畫得再細也不會比一個好認的輪廓更像他。字就刻在臉上，位置正好是
 * 他的米白口鼻。
 *
 * 未達成的整顆退成灰的、輪廓改虛線。
 */
function CatBadge({ badge }: { badge: Badge }) {
  return (
    <svg viewBox="0 0 50 50" class="badge-face" aria-hidden="true">
      {/* 位移的實心影子。全 app 的硬陰影都是這個樣子，這裡照著頭型畫一份 */}
      <path class="badge-shadow" d={HEAD_PATH} transform="translate(1.6 1.8)" />

      <path class="badge-fur" d={HEAD_PATH} />

      {/* 耳朵內側的粉紅。跟著耳朵的形狀縮一圈 */}
      <path class="badge-ear-inner" d="M6.2 12.6 C6.6 9.2 7.8 7.6 9.6 8.6 C11.2 9.4 12.6 10.8 14.2 12.2 C10.6 13.8 7.6 13.8 6.2 12.6 Z" />
      <path class="badge-ear-inner" d="M43.8 12.6 C43.4 9.2 42.2 7.6 40.4 8.6 C38.8 9.4 37.4 10.8 35.8 12.2 C39.4 13.8 42.4 13.8 43.8 12.6 Z" />

      {/* 刻字的地方就是他的米白口鼻 */}
      <rect class="badge-plate" x="11" y="24" width="28" height="13" rx="6.5" />
      <text class="badge-mark" x="25" y="33.2" text-anchor="middle">{badge.mark}</text>
    </svg>
  );
}

export interface BadgeWallProps {
  list: Badge[];
  /** 目前展開的徽章 id，沒有展開是 null。 */
  openId: string | null;
  onToggle: (id: string) => void;
}

export function BadgeWall({ list, openId, onToggle }: BadgeWallProps) {
  const earned = list.filter(item => item.earned).length;
  const open = list.find(item => item.id === openId) || null;

  return (
    <article class="badge-wall brutal-card">
      <div class="badge-wall-head">
        <div><h2>成就徽章</h2><p>拿到就不會掉。體重類以 7 日平均判定，不看單日。</p></div>
        <strong>{earned} / {list.length}</strong>
      </div>

      <div id="badgeWall" class="badge-grid">
        {list.map(item => (
          <button
            key={item.id}
            type="button"
            data-badge={item.id}
            class={`badge-cat ${item.kind} ${item.earned ? "earned" : "locked"} ${item.id === openId ? "open" : ""}`}
            aria-pressed={item.id === openId}
            aria-label={item.label}
            onClick={() => onToggle(item.id)}
          >
            <CatBadge badge={item} />
          </button>
        ))}
      </div>

      {/*
        細節固定一列，展開哪一枚都不會讓卡片抽動——高度會跳的清單在手機上
        很難點第二下。
      */}
      <div id="badgeDetail" class="badge-detail">
        {open ? (
          <>
            <b>{open.label}</b>
            <small>
              {open.earned ? `達成於 ${formatDate(open.earnedOn)}・` : "還沒拿到・"}
              {open.condition}
            </small>
          </>
        ) : (
          <small class="badge-detail-hint">點一下徽章，看它是怎麼拿到的。</small>
        )}
      </div>
    </article>
  );
}
